import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useState, useMemo } from 'react';
import { getFieldsByCategory, FieldDefinition } from '../../lib/fieldRegistry';
import type { FieldType } from '../../types/form';

interface DraggableFieldProps {
  type: FieldType;
  label: string;
  icon: string;
}

function DraggableField({ type, label, icon }: DraggableFieldProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `palette-${type}`,
    data: { type, isNew: true },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-grab hover:border-primary-300 dark:hover:border-primary-500 hover:shadow-sm transition-all active:cursor-grabbing"
    >
      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded text-lg">
        {icon}
      </span>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
    </div>
  );
}

export default function FieldPalette() {
  const [searchTerm, setSearchTerm] = useState('');
  const basicFields = getFieldsByCategory('basic');
  const advancedFields = getFieldsByCategory('advanced');
  const layoutFields = getFieldsByCategory('layout');

  // Filter fields based on search term
  const filterFields = (fields: FieldDefinition[]) => {
    if (!searchTerm.trim()) return fields;
    const term = searchTerm.toLowerCase();
    return fields.filter(field =>
      field.label.toLowerCase().includes(term) ||
      field.type.toLowerCase().includes(term)
    );
  };

  const filteredBasic = useMemo(() => filterFields(basicFields), [searchTerm, basicFields]);
  const filteredAdvanced = useMemo(() => filterFields(advancedFields), [searchTerm, advancedFields]);
  const filteredLayout = useMemo(() => filterFields(layoutFields), [searchTerm, layoutFields]);

  const hasResults = filteredBasic.length > 0 || filteredAdvanced.length > 0 || filteredLayout.length > 0;

  return (
    <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
        Field Types
      </h2>

      {/* Search Input */}
      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {!hasResults && searchTerm && (
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
          No fields match "{searchTerm}"
        </div>
      )}

      {/* Basic Fields */}
      {filteredBasic.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Basic</h3>
          <div className="space-y-2">
            {filteredBasic.map((field) => (
              <DraggableField
                key={field.type}
                type={field.type}
                label={field.label}
                icon={field.icon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Advanced Fields */}
      {filteredAdvanced.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Advanced</h3>
          <div className="space-y-2">
            {filteredAdvanced.map((field) => (
              <DraggableField
                key={field.type}
                type={field.type}
                label={field.label}
                icon={field.icon}
              />
            ))}
          </div>
        </div>
      )}

      {/* Layout Fields */}
      {filteredLayout.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Layout</h3>
          <div className="space-y-2">
            {filteredLayout.map((field) => (
              <DraggableField
                key={field.type}
                type={field.type}
                label={field.label}
                icon={field.icon}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
